// M1 THROWAWAY (OPTI-6761): per-mount chat against a freshly-provisioned
// Live Analytics agent. The FE owns the system prompt and the full lifecycle:
// on first send we create+publish a new agent, then invoke and poll. No
// cross-reload persistence — closing or reloading drops the agent on purpose
// so each conversation starts from a clean prompt. Replace once the proper
// agent lifecycle lands.

import { useCallback, useRef, useState } from "react";

import { v4 as uuidv4 } from "uuid";

import { agentExecutionApi, agentManagementApi } from "src/api/endpoints";
import { useCurrentWorkspace } from "src/api/queries";
import {
  type AgentVersionConfigurationInputToolsInner,
  AgentVersionConfigurationInputToolsInnerTypeEnum,
  type Content,
  type Data,
  Environment,
  ExecutionMode,
  type HumanMessage,
  HumanMessageTypeEnum,
  Model,
  type SessionMessagesResponseMessagesInner,
  SessionStatus,
} from "src/clients/agent-management";
import exampleResponse from "src/flow/analytics/exampleResponse.json";
import { buildLiveAnalyticsSystemPrompt } from "src/flow/analytics/m1/liveAnalyticsSystemPrompt";
import { fetchWorkspaceSchemaBlock } from "src/flow/analytics/m1/workspaceSchemaSnapshot";

// Gate on MODE === "development" rather than DEV so the seed only fires
// under the dev server. DEV is also true under Vitest, which would defeat
// empty-state assertions in CopilotSidebar tests.
const DEV_SEED_MESSAGES: SessionMessagesResponseMessagesInner[] =
  import.meta.env.MODE === "development"
    ? (exampleResponse.messages as unknown as SessionMessagesResponseMessagesInner[])
    : [];

const SLUG_PREFIX = "live-analytics-m1-";
const AGENT_NAME = "Live Analytics (M1)";
const AGENT_DESCRIPTION =
  "Auto-provisioned per-session Live Analytics chat agent (OPTI-6761 throwaway)";
const SESSION_POLL_MS = 1500;
const SESSION_POLL_MAX_ATTEMPTS = 80;

export type LiveAnalyticsChatStatus =
  | "idle"
  | "provisioning"
  | "thinking"
  | "done"
  | "error";

const abortableSleep = (ms: number, signal: AbortSignal): Promise<void> =>
  new Promise((resolve) => {
    if (signal.aborted) {
      resolve();
      return;
    }
    const timer = setTimeout(resolve, ms);
    signal.addEventListener(
      "abort",
      () => {
        clearTimeout(timer);
        resolve();
      },
      { once: true },
    );
  });

// Tools the prototype uses. The auto-generated TS type requires flow_slug etc.
// that the backend only enforces for FLOW tools — for query_metrics /
// reply_visualize_metrics the backend accepts the minimal shape used here,
// so we cast through `unknown`. Matches the pattern in useCaseCopilotInvoke.
const buildTools = (): AgentVersionConfigurationInputToolsInner[] =>
  [
    {
      id: uuidv4(),
      type: AgentVersionConfigurationInputToolsInnerTypeEnum.QUERY_METRICS,
      name: "query_metrics",
      description:
        "Execute SQL queries against flow_data_prototype via DuckDB. Send {queries: {name: 'SELECT ...'}}",
    },
    {
      id: uuidv4(),
      type: AgentVersionConfigurationInputToolsInnerTypeEnum.REPLY_VISUALIZE_METRICS,
      name: "reply_visualize_metrics",
      description:
        "Submit a SQL query and a Vega-Lite v5 chart_definition (JSON object) for user review. The frontend executes the SQL and injects the rows into the spec — do NOT include a `data` block.",
      chart_format: "vega",
    },
  ] as unknown as AgentVersionConfigurationInputToolsInner[];

type ChatState = {
  status: LiveAnalyticsChatStatus;
  errorMessage: string | null;
  agentId: string | null;
  sessionId: string | null;
  messages: SessionMessagesResponseMessagesInner[];
  pendingUserMessage: HumanMessage | null;
};

export const useLiveAnalyticsChat = ({
  flowId,
  flowName,
}: {
  flowId: string;
  flowName: string;
}): {
  status: LiveAnalyticsChatStatus;
  errorMessage: string | null;
  sessionId: string | null;
  messages: SessionMessagesResponseMessagesInner[];
  send: (message: string) => Promise<void>;
  resetSession: () => void;
} => {
  const { data: workspace } = useCurrentWorkspace();
  const baseUrl = workspace?.base_url;

  const abortRef = useRef<AbortController | null>(null);

  const [state, setState] = useState<ChatState>({
    status: "idle",
    errorMessage: null,
    agentId: null,
    sessionId: null,
    messages: DEV_SEED_MESSAGES,
    pendingUserMessage: null,
  });

  const provisionAgent = useCallback(
    async (workspaceBaseUrl: string): Promise<string> => {
      const mgmt = agentManagementApi(workspaceBaseUrl);

      const workspaceSchemaBlock = await fetchWorkspaceSchemaBlock(
        workspaceBaseUrl,
        flowId,
      ).catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : String(err);
        return `_(schema snapshot unavailable — ${msg}; agent will discover keys on demand)_`;
      });

      const systemPrompt = buildLiveAnalyticsSystemPrompt({
        flowId,
        flowName,
        workspaceSchemaBlock,
      });

      const slug = SLUG_PREFIX + uuidv4().slice(0, 8);
      const createResp =
        await mgmt.createAgentCopilotsManagementApiV0AgentsPost({
          agentCreate: {
            slug,
            name: AGENT_NAME,
            description: AGENT_DESCRIPTION,
          },
        });
      const agentId = createResp.data.agent.id;

      const versionResp =
        await mgmt.createVersionCopilotsManagementApiV0AgentsAgentIdVersionsPost(
          {
            agentId,
            agentVersionCreate: {
              name: "v1",
              description: "Auto-provisioned M1 Live Analytics version",
              configuration: {
                model_config: {
                  model: Model.CLAUDE_4_5_SONNET,
                  system_prompt: systemPrompt,
                  temperature: 0,
                },
                tools: buildTools(),
              },
            },
          },
        );
      const versionId = versionResp.data.agent_version.id;

      await mgmt.publishVersionCopilotsManagementApiV0AgentsAgentIdVersionsVersionIdPublishPost(
        {
          agentId,
          versionId,
          ifMatch: "0",
        },
      );

      return agentId;
    },
    [flowId, flowName],
  );

  const pollSession = useCallback(
    async (
      workspaceBaseUrl: string,
      sessionId: string,
      startingStatus: SessionStatus,
      attemptsLeft: number,
      signal: AbortSignal,
    ): Promise<SessionStatus> => {
      const terminal =
        startingStatus === SessionStatus.SUCCESS ||
        startingStatus === SessionStatus.FAILED ||
        startingStatus === SessionStatus.CANCELLED;
      if (terminal || attemptsLeft === 0 || signal.aborted)
        return startingStatus;
      await abortableSleep(SESSION_POLL_MS, signal);
      if (signal.aborted) return startingStatus;
      const exec = agentExecutionApi(workspaceBaseUrl);
      const resp =
        await exec.getSessionCopilotsManagementApiV0SessionsSessionIdGet({
          sessionId,
          includeMessages: true,
        });
      if (signal.aborted) return resp.data.status;
      const serverMessages = resp.data.messages ?? [];
      setState((prev) => ({
        ...prev,
        messages: serverMessages.length > 0 ? serverMessages : prev.messages,
        pendingUserMessage:
          serverMessages.length > 0 ? null : prev.pendingUserMessage,
      }));
      return pollSession(
        workspaceBaseUrl,
        sessionId,
        resp.data.status,
        attemptsLeft - 1,
        signal,
      );
    },
    [],
  );

  const send = useCallback(
    async (message: string): Promise<void> => {
      if (!baseUrl) {
        setState((prev) => ({
          ...prev,
          status: "error",
          errorMessage: "Workspace not loaded",
        }));
        return;
      }
      const trimmed = message.trim();
      if (!trimmed) return;

      // Optimistic echo so the user sees their own message before invoke
      // responds. Cleared on the first non-empty poll response by pollSession.
      const optimistic: HumanMessage = {
        type: HumanMessageTypeEnum.HUMAN,
        id: `pending-${uuidv4()}`,
        content: trimmed as unknown as Content,
        token_count: 0,
      };
      setState((prev) => ({ ...prev, pendingUserMessage: optimistic }));

      abortRef.current?.abort();
      const abort = new AbortController();
      abortRef.current = abort;

      const currentAgentId = state.agentId;

      const ensureAgentId = async (): Promise<string | null> => {
        if (currentAgentId) return currentAgentId;
        setState((prev) => ({
          ...prev,
          status: "provisioning",
          errorMessage: null,
        }));
        try {
          const newAgentId = await provisionAgent(baseUrl);
          setState((prev) => ({ ...prev, agentId: newAgentId }));
          return newAgentId;
        } catch (err: unknown) {
          const msg = err instanceof Error ? err.message : String(err);
          setState((prev) => ({
            ...prev,
            status: "error",
            errorMessage: `Provision failed: ${msg}`,
            pendingUserMessage: null,
          }));
          return null;
        }
      };

      const agentId = await ensureAgentId();
      if (!agentId) return;

      setState((prev) => ({ ...prev, status: "thinking", errorMessage: null }));

      // Multi-turn sessions require the client to mint the session_id on
      // the first invoke and reuse it on every follow-up — same pattern as
      // useCaseCopilotInvoke. Sending the first invoke without a session_id
      // makes the backend mint a single-turn session that terminates after
      // one reply, breaking subsequent turns.
      const sessionId =
        state.sessionId ??
        (() => {
          const newId = uuidv4();
          setState((prev) => ({ ...prev, sessionId: prev.sessionId ?? newId }));
          return newId;
        })();

      // The generated `Data` type unions all invoke variants and marks
      // playground/flow-only fields as required. Cast matches the pattern in
      // useCaseCopilotInvoke.
      const invokeData = {
        agent_id: agentId,
        agent_version_id: "latest",
        invocation_message: trimmed,
        session_id: sessionId,
        type: "invoke",
        source: "api",
        control: {
          execution_mode: ExecutionMode.ASYNC,
          environment: Environment.SANDBOX,
        },
      } as unknown as Data;

      try {
        const exec = agentExecutionApi(baseUrl);
        const invokeResp =
          await exec.invokeAgentCopilotsManagementApiV0InvokePost({
            data: invokeData,
          });

        const finalStatus = await pollSession(
          baseUrl,
          sessionId,
          invokeResp.data.status,
          SESSION_POLL_MAX_ATTEMPTS,
          abort.signal,
        );

        if (abort.signal.aborted) return;

        if (finalStatus === SessionStatus.SUCCESS) {
          setState((prev) => ({ ...prev, status: "done" }));
        } else {
          const reason =
            finalStatus === SessionStatus.FAILED
              ? "Agent run failed"
              : finalStatus === SessionStatus.CANCELLED
                ? "Agent run cancelled"
                : `Agent still ${finalStatus} after polling timeout`;
          setState((prev) => ({
            ...prev,
            status: "error",
            errorMessage: reason,
            pendingUserMessage: null,
          }));
        }
      } catch (err: unknown) {
        if (abort.signal.aborted) return;
        const msg = err instanceof Error ? err.message : String(err);
        setState((prev) => ({
          ...prev,
          status: "error",
          errorMessage: `Invoke failed: ${msg}`,
          pendingUserMessage: null,
        }));
      }
    },
    [baseUrl, pollSession, provisionAgent, state.agentId, state.sessionId],
  );

  const resetSession = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setState((prev) => ({
      ...prev,
      status: "idle",
      errorMessage: null,
      sessionId: null,
      messages: [],
      pendingUserMessage: null,
    }));
  }, []);

  return {
    status: state.status,
    errorMessage: state.errorMessage,
    sessionId: state.sessionId,
    messages: state.pendingUserMessage
      ? [
          ...state.messages,
          {
            ...state.pendingUserMessage,
            type: HumanMessageTypeEnum.HUMAN,
          },
        ]
      : state.messages,
    send,
    resetSession,
  };
};
